// T3-B sponsored motif (PR 7-CMS, 2026-05-07):
// campaign 効果計測 dashboard sheet。
//   - 期間 KPI: acquisitions / coupon_used / cv_rate
//   - by_motif: 表
//   - by_fortune: 5 段水平バー
//   - by_day: 日次水平バー (期間内 0 件日も含む、簡易 sparkline)
//   - GA4 dashboard への外部リンク (sponsor_modal_view / sponsor_cta_click は GA4 のみ)
//
// auth: ADMIN only (router 側で enforce)。
// Spec: design/sponsored-motif-spec.md §11 / §6.1

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { FORTUNE_LABEL } from '@/utils/omikujiLabels';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

type Props = {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    campaignId: number | null
};

const formatPct = (n?: number | null): string => {
    if (n == null) return '-';
    return `${(n * 100).toFixed(1)}%`;
};

export default observer(function StatsSheet({ open, onOpenChange, campaignId }: Props) {
    const { sponsoredCampaignStore: { stats, getStats } } = useStore();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !campaignId) return;
        setLoading(true);
        flowResult(getStats({ id: campaignId })).finally(() => setLoading(false));
    }, [open, campaignId]);

    const totals = stats?.totals;
    const byMotif: any[] = stats?.by_motif ?? [];
    const byFortune: Record<string, number> = stats?.by_fortune ?? {};
    const byDay: { date: string, acquisitions: number }[] = stats?.by_day ?? [];

    const motifMaxAcq = Math.max(1, ...byMotif.map(m => m.acquisitions ?? 0));
    const fortuneMax = Math.max(1, ...Object.values(byFortune).map(v => Number(v) || 0));
    const dayMax = Math.max(1, ...byDay.map(d => d.acquisitions ?? 0));

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-full sm:max-w-3xl overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>
                        効果計測 —
                        {' '}
                        {stats?.campaign?.name ?? ''}
                    </SheetTitle>
                </SheetHeader>

                {loading && !stats && (
                    <div className='py-12 text-center text-gray-500'>
                        読み込み中...
                    </div>
                )}

                {stats && (
                    <div className='mt-4 space-y-6'>
                        {/* 期間 + sponsor */}
                        <div className='text-xs text-gray-600'>
                            <div>
                                スポンサー:
                                {' '}
                                {stats?.campaign?.sponsor?.name ?? '-'}
                            </div>
                            <div>
                                期間:
                                {' '}
                                {stats?.period?.from ?? '-'}
                                {' '}
                                〜
                                {' '}
                                {stats?.period?.to ?? '-'}
                                {' '}
                                (UTC)
                            </div>
                            <div>
                                ステータス:
                                {' '}
                                {stats?.campaign?.status ?? '-'}
                                {' '}
                                / 種別:
                                {' '}
                                {stats?.campaign?.campaign_type ?? '-'}
                            </div>
                        </div>

                        {/* KPI cards */}
                        <div className='grid grid-cols-3 gap-3'>
                            <div className='border rounded-md p-3 bg-orange-50'>
                                <div className='text-xs text-gray-600'>取得総数</div>
                                <div className='text-2xl font-bold text-orange-700 mt-1'>
                                    {totals?.acquisitions ?? 0}
                                </div>
                            </div>
                            <div className='border rounded-md p-3 bg-emerald-50'>
                                <div className='text-xs text-gray-600'>クーポン使用</div>
                                <div className='text-2xl font-bold text-emerald-700 mt-1'>
                                    {totals?.coupon_used ?? 0}
                                </div>
                            </div>
                            <div className='border rounded-md p-3 bg-sky-50'>
                                <div className='text-xs text-gray-600'>CVR</div>
                                <div className='text-2xl font-bold text-sky-700 mt-1'>
                                    {formatPct(totals?.cv_rate)}
                                </div>
                            </div>
                        </div>

                        {/* by_motif */}
                        <div>
                            <h3 className='text-sm font-semibold mb-2'>モチーフ別</h3>
                            {byMotif.length === 0 ?
                                (
                                    <div className='text-xs text-gray-500 py-3'>モチーフがまだ登録されていません</div>
                                ) :
                                (
                                    <div className='border rounded-md overflow-hidden'>
                                        <table className='w-full text-xs'>
                                            <thead className='bg-gray-100'>
                                                <tr>
                                                    <th className='py-2 px-3 text-left font-semibold'>モチーフ名</th>
                                                    <th className='py-2 px-3 text-left font-semibold'>階層</th>
                                                    <th className='py-2 px-3 text-left font-semibold'>排出率</th>
                                                    <th className='py-2 px-3 text-right font-semibold'>取得数</th>
                                                    <th className='py-2 px-3 text-right font-semibold'>使用数</th>
                                                    <th className='py-2 px-3 text-left font-semibold w-[30%]'>取得分布</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {byMotif.map((m: any) => (
                                                    <tr key={m.sponsored_motif_id} className='border-t'>
                                                        <td className='py-2 px-3'>{m.name}</td>
                                                        <td className='py-2 px-3'>{FORTUNE_LABEL[m.fortune_tier] ?? m.fortune_tier}</td>
                                                        <td className='py-2 px-3'>{formatPct(m.spawn_rate)}</td>
                                                        <td className='py-2 px-3 text-right'>{m.acquisitions}</td>
                                                        <td className='py-2 px-3 text-right'>{m.coupon_used}</td>
                                                        <td className='py-2 px-3'>
                                                            <div className='h-2 bg-gray-100 rounded'>
                                                                <div
                                                                    className='h-full bg-orange-400 rounded'
                                                                    style={{ width: `${(m.acquisitions / motifMaxAcq) * 100}%` }}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                        </div>

                        {/* by_fortune */}
                        <div>
                            <h3 className='text-sm font-semibold mb-2'>占い階層別</h3>
                            <div className='space-y-1'>
                                {[1, 2, 3, 4, 5].map((f) => {
                                    const count = Number(byFortune[String(f)] ?? byFortune[f] ?? 0);
                                    const pct = (count / fortuneMax) * 100;
                                    return (
                                        <div key={f} className='flex items-center gap-3 text-xs'>
                                            <div className='w-12 text-gray-700 font-semibold'>{FORTUNE_LABEL[f]}</div>
                                            <div className='flex-1 h-2.5 bg-gray-100 rounded'>
                                                <div
                                                    className='h-full bg-sky-400 rounded'
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                            <div className='w-12 text-right text-gray-700'>{count}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* by_day */}
                        <div>
                            <h3 className='text-sm font-semibold mb-2'>日次推移</h3>
                            {byDay.length === 0 ?
                                (
                                    <div className='text-xs text-gray-500 py-3'>期間内データなし</div>
                                ) :
                                (
                                    <div className='space-y-0.5'>
                                        {byDay.map(d => (
                                            <div key={d.date} className='flex items-center gap-3 text-xs'>
                                                <div className='w-24 text-gray-600 font-mono'>{d.date}</div>
                                                <div className='flex-1 h-2 bg-gray-100 rounded'>
                                                    <div
                                                        className='h-full bg-orange-400 rounded'
                                                        style={{ width: `${(d.acquisitions / dayMax) * 100}%` }}
                                                    />
                                                </div>
                                                <div className='w-10 text-right text-gray-700'>{d.acquisitions}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                        </div>

                        {/* GA4 reference (sponsor_modal_view / sponsor_cta_click は GA4 のみ) */}
                        <div className='border-t pt-3 text-xs text-gray-500'>
                            <div className='font-semibold text-gray-700 mb-1'>表示数 / CTA タップ数</div>
                            <p>
                                {stats?.ga4?.note ?? 'sponsor_modal_view / sponsor_cta_click は GA4 dashboard で確認'}
                            </p>
                            <p className='mt-1 text-gray-400'>
                                (campaign_id=
                                {stats?.campaign?.id}
                                {' '}
                                で GA4 のカスタム dimension をフィルタ)
                            </p>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
});
